import AppTheme from "@toothfairy/shared-ui/AppTheme";
import React, { useMemo } from "react";
import { useWindowDimensions, View, ScrollView } from "react-native";
import Charting from "../../../Components/Charting";
import ArenaHeader from "../Components/ArenaHeader";
import DashboardItem from "../../Dashboard/Components/DashboardItem";
import DashboardPlaceholder from "../../Dashboard/Components/DashboardPlaceholder";
import { AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import { useTranslation } from "react-i18next";

const DashboardScreen = ({ dashboardData, winsOverTimeData }) => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const { width, height } = useWindowDimensions();

  const isLargeScreen = width >= 1200;

  const doughnutChartOptions = {
    legend: {
      labels: {
        color: currentTheme?.theme?.font_color,
      },
    },
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        display: true,
        labels: {
          color: currentTheme?.theme?.font_color,
          usePointStyle: true,
          pointStyle: "circle",
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        mode: "index",
        intersect: false,
      },
    },
    scales: {
      x: {
        ticks: {
          color: currentTheme?.theme?.font_color,
        },
        grid: {
          color: currentTheme?.theme?.border_color,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          color: currentTheme?.theme?.font_color,
          stepSize: 1,
        },
        grid: {
          color: currentTheme?.theme?.border_color,
        },
      },
    },
  };

  const horizontalBarOptions = {
    indexAxis: "y",
    plugins: {
      title: {
        display: false,
      },
      legend: {
        display: false,
        labels: {
          color: currentTheme?.theme?.font_color,
        },
      },
    },
    scales: {
      x: {
        display: true,
        position: "bottom",
        ticks: {
          color: currentTheme?.theme?.font_color,
        },
        grid: {
          color: currentTheme?.theme?.border_color,
        },
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          color: currentTheme?.theme?.font_color,
        },
      },
    },
  };

  const colors = [
    currentTheme?.theme?.primary,
    currentTheme?.theme?.orange,
    currentTheme?.theme?.red,
    currentTheme?.theme?.purple,
    currentTheme?.theme?.yellow,
    currentTheme?.theme?.green,
    "#1abc9c",
    "#e74c3c",
    "#3498db",
    "#9b59b6",
  ];

  const agentsWithWins = useMemo(
    () =>
      dashboardData?._leaderBoardAgents?.filter((agent) => agent?.wins > 0) ||
      [],
    [dashboardData?._leaderBoardAgents],
  );

  const hasLeaderboardData = agentsWithWins && agentsWithWins.length > 0;
  const hasWinsOverTimeData =
    winsOverTimeData && Object.keys(winsOverTimeData).length > 0;

  const dates = hasWinsOverTimeData
    ? Object.keys(winsOverTimeData).sort((a, b) => new Date(a) - new Date(b))
    : [];

  const agentsForLineChart = useMemo(
    () =>
      dashboardData?._leaderBoardAgents?.filter((agent) => {
        const hasAnyWins = dates.some(
          (date) => (winsOverTimeData[date]?.[agent.id] || 0) > 0,
        );
        return hasAnyWins;
      }) || [],
    [dashboardData?._leaderBoardAgents, dates, winsOverTimeData],
  );

  const agentsWithBattles = useMemo(
    () =>
      dashboardData?._leaderBoardAgents?.filter(
        (agent) => agent?.total_battles > 0,
      ) || [],
    [dashboardData?._leaderBoardAgents],
  );

  const lineChartData = hasWinsOverTimeData
    ? {
        labels: dates,
        datasets: agentsForLineChart?.map((agent, index) => ({
          label: agent?.label,
          data: dates?.map((date) => winsOverTimeData[date]?.[agent.id] || 0),
          borderColor: colors[index % colors.length],
          backgroundColor: colors[index % colors.length] + "40",
          tension: 0.3,
          fill: false,
          pointRadius: 4,
          pointHoverRadius: 6,
        })),
      }
    : null;

  const winRateData = useMemo(() => {
    if (!agentsWithBattles || agentsWithBattles.length === 0) return null;

    return {
      labels: agentsWithBattles?.map((agent) => agent?.label),
      datasets: [
        {
          label: "Win Rate %",
          data: agentsWithBattles?.map((agent) => {
            const total = agent?.total_battles || 0;
            const wins = agent?.wins || 0;
            return total > 0 ? Math.round((wins / total) * 100) : 0;
          }),
          borderColor: currentTheme?.theme?.primary,
          borderRadius: 10,
          backgroundColor: currentTheme?.theme?.primary,
          maxBarThickness: 40,
        },
      ],
    };
  }, [agentsWithBattles, currentTheme?.theme?.primary]);

  const totalBattles = dashboardData?.total_battles || 0;
  const totalWins = dashboardData?.tf_wins || 0;
  const totalDraws = dashboardData?.tf_draws || 0;
  const overallWinRate =
    totalBattles > 0 ? Math.round((totalWins / totalBattles) * 100) : 0;

  const getQuadrantWidth = () => {
    if (isLargeScreen) return `calc(50% - 10px)`;
    return "100%";
  };

  return (
    <View
      style={{
        display: "flex",
        flex: 1,
        height: height - 80,
      }}
    >
      <ArenaHeader />

      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{
          padding: 20,
        }}
      >
        <View
          style={{
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 20,
          }}
        >
          <View
            style={{
              width: getQuadrantWidth(),
            }}
          >
            <DashboardItem
              customWidth={"auto"}
              title={t("arena.leaderboard.winsByAgents")}
            >
              {hasLeaderboardData ? (
                <Charting.AppDoughnut
                  options={doughnutChartOptions}
                  maxHeight={350}
                  dataset={{
                    labels: agentsWithWins?.map((u) => u?.label),
                    datasets: [
                      {
                        label: t("arena.leaderboard.wins"),
                        data: agentsWithWins?.map((u) => u?.wins),
                        backgroundColor: colors,
                      },
                    ],
                  }}
                />
              ) : (
                <DashboardPlaceholder
                  mainCaption={t("arena.leaderboard.noBattlesYet")}
                  subCaption={t("arena.leaderboard.completeBattles")}
                />
              )}
            </DashboardItem>
          </View>

          <View
            style={{
              width: getQuadrantWidth(),
            }}
          >
            <DashboardItem
              customWidth={"auto"}
              title={t("arena.leaderboard.winsOverTime")}
            >
              {hasWinsOverTimeData &&
              lineChartData &&
              agentsForLineChart.length > 0 ? (
                <View
                  style={{
                    maxHeight: 350,
                    width: "-webkit-fill-available",
                    height: "-webkit-fill-available",
                  }}
                >
                  <Charting.AppLineChart
                    dataset={lineChartData}
                    options={lineChartOptions}
                    c
                  />
                </View>
              ) : (
                <DashboardPlaceholder
                  mainCaption={t("arena.leaderboard.noDataAvailable")}
                  subCaption={t("arena.leaderboard.winsOverTimeCaption")}
                />
              )}
            </DashboardItem>
          </View>

          <View
            style={{
              width: getQuadrantWidth(),
            }}
          >
            <DashboardItem
              customWidth={"auto"}
              title={t("arena.leaderboard.winRateComparison")}
            >
              {agentsWithBattles && agentsWithBattles.length > 0 ? (
                <Charting.AppBarChart
                  vertical={false}
                  maxHeight={360}
                  options={horizontalBarOptions}
                  dataset={winRateData}
                  customWidth={"auto"}
                />
              ) : (
                <DashboardPlaceholder
                  mainCaption={t("arena.leaderboard.noDataAvailable")}
                  subCaption={t("arena.leaderboard.winRatesCaption")}
                />
              )}
            </DashboardItem>
          </View>

          <View
            style={{
              width: getQuadrantWidth(),
            }}
          >
            <DashboardItem
              customWidth={"auto"}
              title={t("arena.leaderboard.battlesOverview")}
            >
              <View style={{ padding: 20 }}>
                <View
                  style={{
                    flexDirection: "row",
                    justifyContent: "space-around",
                    marginBottom: 30,
                    flexWrap: "wrap",
                    gap: 20,
                  }}
                >
                  <StatCard
                    icon={AppAssets?.icons?.dashboard}
                    label={t("arena.leaderboard.totalBattles")}
                    value={totalBattles}
                    color={currentTheme?.theme?.primary}
                  />
                  <StatCard
                    icon={AppAssets?.icons?.thumbsUp}
                    label={t("arena.leaderboard.totalWins")}
                    value={totalWins}
                    color={currentTheme?.theme?.green}
                  />
                  <StatCard
                    icon={AppAssets?.icons?.handshake}
                    label={t("arena.leaderboard.draws")}
                    value={totalDraws}
                    color={currentTheme?.theme?.orange}
                  />
                  <StatCard
                    icon={AppAssets?.icons?.benchmark}
                    label={t("arena.leaderboard.winRate")}
                    value={`${overallWinRate}%`}
                    color={currentTheme?.theme?.primary}
                  />
                </View>

                {agentsWithBattles && agentsWithBattles.length > 0 && (
                  <View>
                    <AppText
                      preventTranslation
                      style={{
                        fontWeight: "bold",
                        marginBottom: 15,
                        fontSize: 16,
                      }}
                    >
                      {t("arena.leaderboard.agentStatsTop5")}
                    </AppText>
                    {agentsWithBattles
                      ?.sort((a, b) => (b?.wins || 0) - (a?.wins || 0))
                      ?.slice(0, 5)
                      ?.map((agent, index) => (
                        <View
                          key={agent?.id || index}
                          style={{
                            flexDirection: "row",
                            justifyContent: "space-between",
                            alignItems: "center",
                            paddingVertical: 10,
                            borderBottomWidth: 1,
                            borderBottomColor:
                              currentTheme?.theme?.border_color,
                          }}
                        >
                          <AppText
                            preventTranslation
                            style={{
                              flex: 1,
                              fontWeight: "600",
                            }}
                          >
                            {agent?.label}
                          </AppText>
                          <View
                            style={{
                              flexDirection: "row",
                              gap: 20,
                            }}
                          >
                            <AppText
                              preventTranslation
                              style={{ color: currentTheme?.theme?.green }}
                            >
                              {t("arena.leaderboard.winsShort", {
                                count: agent?.wins || 0,
                              })}
                            </AppText>
                            <AppText
                              preventTranslation
                              style={{ color: currentTheme?.theme?.orange }}
                            >
                              {t("arena.leaderboard.drawsShort", {
                                count: agent?.draws || 0,
                              })}
                            </AppText>
                            <AppText
                              preventTranslation
                              style={{ color: currentTheme?.theme?.font_color }}
                            >
                              {t("arena.leaderboard.battlesShort", {
                                count: agent?.total_battles || 0,
                              })}
                            </AppText>
                          </View>
                        </View>
                      ))}
                  </View>
                )}
              </View>
            </DashboardItem>
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const StatCard = ({ icon, label, value, color }) => {
  const { currentTheme } = AppTheme.useTheme();

  return (
    <View
      style={{
        alignItems: "center",
        minWidth: 100,
      }}
    >
      <AppText
        preventTranslation
        leftIcon={icon}
        iconSize={28}
        style={{
          color: color,
          fontWeight: "bold",
          fontSize: 28,
          marginBottom: 5,
        }}
      />
      <AppText
        preventTranslation
        style={{
          fontSize: 24,
          fontWeight: "bold",
          color: color,
        }}
      >
        {value}
      </AppText>
      <AppText
        preventTranslation
        style={{
          fontSize: 12,
          color: currentTheme?.theme?.darkGrey,
          marginTop: 5,
        }}
      >
        {label}
      </AppText>
    </View>
  );
};

export default DashboardScreen;
